import axios from 'axios';
import { TGatewayError } from 'services/api/payments/types';

export const DEFAULT_ERROR_MESSAGE = 'Something went wrong';
const ACTION_BLOCKED_MESSAGE = 'This action is temporarily unavailable.';

/** 423 of the payment gateway: the action is blocked by the operator (client access restrictions). */
export const CLIENT_ACCESS_BLOCKED_STATUS = 423;
export const CLIENT_ACCESS_ERROR_CODES: readonly string[] = [
	'payment_methods_disabled',
	'payments_disabled',
	'pay_link_disabled',
];

const getErrorBody = (error: unknown): TGatewayError | undefined => {
	if (!axios.isAxiosError(error)) return undefined;
	const data = error.response?.data as unknown;
	return data && typeof data === 'object' ? (data as TGatewayError) : undefined;
};

/** `errorCode` of the payment gateway error body (snake_case), if any. */
export const getErrorCode = (error: unknown): string | undefined => getErrorBody(error)?.errorCode;

export const getErrorDetails = (error: unknown): Record<string, unknown> | undefined =>
	getErrorBody(error)?.details ?? undefined;

export const getErrorStatus = (error: unknown): number | undefined =>
	axios.isAxiosError(error) ? error.response?.status : undefined;

/** `true` — the gateway refused the call because the operator blocked it (423 with a client access errorCode). */
export const isClientAccessBlockedError = (error: unknown): boolean => {
	const code = getErrorCode(error);
	return (
		getErrorStatus(error) === CLIENT_ACCESS_BLOCKED_STATUS &&
		!!code &&
		CLIENT_ACCESS_ERROR_CODES.includes(code)
	);
};

/** Text for the user: `message` of the error body, otherwise a generic text. */
export const getErrorMessage = (error: unknown): string => {
	const message = getErrorBody(error)?.message;
	if (typeof message === 'string' && message.trim()) return message;
	return isClientAccessBlockedError(error) ? ACTION_BLOCKED_MESSAGE : DEFAULT_ERROR_MESSAGE;
};

/** New idempotency key (one per opened payment dialog). */
export const newIdempotencyKey = (): string => {
	if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
		return crypto.randomUUID();
	}
	const bytes = new Uint8Array(16);
	crypto.getRandomValues(bytes);
	bytes[6] = (bytes[6] & 0x0f) | 0x40;
	bytes[8] = (bytes[8] & 0x3f) | 0x80;
	const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
	return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
};
